import { getNodeDefinition, NODE_TYPES } from './catalogue';
import type { Endpoint, FlowGraph } from './graph';
import { indexGraph, mismatch, portKey, resolveTypes } from './resolve';
import { DATA_TYPE_LABELS } from './types';

export type ConnectResult = { ok: true } | { ok: false; reason: string };

const refuse = (reason: string): ConnectResult => ({ ok: false, reason });

/**
 * The output to draw an edge from: `source` when it fits, else another of the
 * node's outputs that does (dragged from Posts onto Scene video, it's Images).
 */
export function fittingSource(
  graph: FlowGraph,
  source: Endpoint,
  target: Endpoint,
): Endpoint | undefined {
  if (canConnect(graph, source, target).ok) return source;
  const node = graph.nodes.find((n) => n.id === source.nodeId);
  const outputs = node ? (getNodeDefinition(node.type)?.outputs ?? []) : [];
  return outputs
    .filter((port) => port.id !== source.port)
    .map((port) => ({ nodeId: source.nodeId, port: port.id }))
    .find((other) => canConnect(graph, other, target).ok);
}

/**
 * Whether an edge from `source` to `target` may be drawn - the check the
 * editor runs while dragging. `validateGraph` repeats these rules on save.
 */
export function canConnect(
  graph: FlowGraph,
  source: Endpoint,
  target: Endpoint,
): ConnectResult {
  const index = indexGraph(graph);
  const from = index.nodes.get(source.nodeId);
  const to = index.nodes.get(target.nodeId);
  if (!from?.definition || !to?.definition) return refuse('Unknown node');
  if (source.nodeId === target.nodeId) return refuse('A node can’t connect to itself');

  const outPort = from.definition.outputs.find((port) => port.id === source.port);
  const inPort = to.definition.inputs.find((port) => port.id === target.port);
  if (!outPort || !inPort) return refuse('Unknown port');

  const duplicate = graph.edges.some(
    (edge) =>
      edge.source.nodeId === source.nodeId &&
      edge.source.port === source.port &&
      edge.target.nodeId === target.nodeId &&
      edge.target.port === target.port,
  );
  if (duplicate) return refuse('Already connected');
  if (reaches(index, target.nodeId, source.nodeId))
    return refuse('That would make a loop');

  const types = resolveTypes(graph, index);
  // An unresolved `same` port still has a known type when it's fixed.
  const type =
    types.get(portKey(source.nodeId, source.port)) ??
    (outPort.type === 'trigger' ? 'trigger' : undefined);
  const reason = mismatch(inPort, type, to.definition);
  if (reason) {
    // From a starter, say which starters would fit.
    const accepts = inPort.accepts;
    const fits =
      from.definition.category === 'starter' && Array.isArray(accepts)
        ? NODE_TYPES.filter(
            (node) =>
              node.category === 'starter' &&
              node.status === 'ga' &&
              node.outputs.some((p) => (accepts as string[]).includes(p.type)),
          ).map((node) => node.label)
        : [];
    return refuse(
      fits.length
        ? `${reason} - start with ${fits.slice(0, -1).join(', ')}${fits.length > 1 ? ' or ' : ''}${fits.at(-1)}`
        : reason,
    );
  }

  // Pass-through nodes need one type in; a second, different one can't pass.
  if (
    type !== undefined &&
    type !== 'trigger' &&
    to.definition.outputs.some((p) => p.type === 'same')
  ) {
    for (const edge of index.incoming.get(target.nodeId) ?? []) {
      const other = types.get(portKey(edge.source.nodeId, edge.source.port));
      if (other && other !== 'trigger' && other.base !== type.base) {
        return refuse(
          `${to.definition.label} already takes ${DATA_TYPE_LABELS[other.base]}; it can’t mix types`,
        );
      }
    }
  }
  return { ok: true };
}

function reaches(
  index: ReturnType<typeof indexGraph>,
  from: string,
  to: string,
): boolean {
  const seen = new Set<string>();
  const stack = [from];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (id === to) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const edge of index.outgoing.get(id) ?? []) stack.push(edge.target.nodeId);
  }
  return false;
}
